/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { getLoginApiUrl } from "utils/auth";

// @mui material components
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// @mui icons
import FacebookIcon from "@mui/icons-material/Facebook";
import GitHubIcon from "@mui/icons-material/GitHub";
import GoogleIcon from "@mui/icons-material/Google";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

function Basic() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [capturedResetToken, setCapturedResetToken] = useState("");
  const [loginError, setLoginError] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const brandColor = "rgba(229, 10, 70, 1)";

  const urlResetToken = searchParams.get("token") || searchParams.get("resetToken") || "";
  const resetToken = capturedResetToken || urlResetToken;
  const mode = resetToken ? "reset" : searchParams.get("mode") || "sign-in"; // sign-in | forgot | reset

  useEffect(() => {
    const rememberedEmail = localStorage.getItem("dsf_remember_email") || "";
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSetRememberMe = () => {
    setRememberMe((prev) => {
      const next = !prev;
      if (!next) {
        localStorage.removeItem("dsf_remember_email");
      } else if (email.trim()) {
        localStorage.setItem("dsf_remember_email", email.trim());
      }
      return next;
    });
  };

  const extractTokenFromLoginResponse = (response) => {
    const data = response?.data || {};
    const headers = response?.headers || {};
    const isLikelyToken = (value) =>
      typeof value === "string" &&
      value.trim().length >= 16 &&
      !value.includes(" ") &&
      !value.includes("@");

    const deepFindToken = (node, depth = 0) => {
      if (!node || depth > 5) return "";

      if (Array.isArray(node)) {
        for (const item of node) {
          const found = deepFindToken(item, depth + 1);
          if (found) return found;
        }
        return "";
      }

      if (typeof node !== "object") {
        return "";
      }

      for (const [key, value] of Object.entries(node)) {
        if (/token|access|jwt|auth/i.test(key) && isLikelyToken(value)) {
          return value.trim();
        }

        if (typeof value === "object") {
          const found = deepFindToken(value, depth + 1);
          if (found) return found;
        }
      }

      return "";
    };

    const tokenCandidates = [
      data?.token,
      data?.accessToken,
      data?.access_token,
      data?.jwt,
      data?.idToken,
      data?.authToken,
      data?.data?.token,
      data?.data?.accessToken,
      data?.data?.access_token,
      data?.data?.jwt,
      data?.data?.idToken,
      data?.data?.authToken,
      data?.result?.token,
      data?.result?.accessToken,
      data?.result?.access_token,
      data?.result?.jwt,
      data?.result?.idToken,
      data?.result?.authToken,
      headers?.authorization?.replace(/^Bearer\s+/i, ""),
      headers?.Authorization?.replace(/^Bearer\s+/i, ""),
      headers?.["x-access-token"],
      headers?.["x-auth-token"],
      headers?.token,
      deepFindToken(data),
    ];

    return tokenCandidates.find((value) => isLikelyToken(value)) || "";
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoginError("");
    setFormMessage("");
    try {
      const normalizedLoginApiUrl = getLoginApiUrl();
      const response = await axios.post(`${normalizedLoginApiUrl}/auth/admin/login`, {
        email: email.trim(),
        password,
      });

      const data = response?.data || {};
      const token = extractTokenFromLoginResponse(response);
      const user = data.user || data.data?.user || { email: email.trim() };

      if (!token) {
        setLoginError(
          "Login successful but token not found. Please share login response format for token key."
        );
        return;
      }
      sessionStorage.setItem("dsf_auth", "authenticated");
      const tokenStorage = rememberMe ? localStorage : sessionStorage;
      const otherStorage = rememberMe ? sessionStorage : localStorage;
      tokenStorage.setItem("dsf_token", token);
      tokenStorage.setItem("token", token);
      tokenStorage.setItem("accessToken", token);
      otherStorage.removeItem("dsf_token");
      otherStorage.removeItem("token");
      otherStorage.removeItem("accessToken");
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      localStorage.setItem("dsf_user", JSON.stringify({ ...user, token }));

      if (rememberMe) {
        localStorage.setItem("dsf_remember_email", email.trim());
      } else {
        localStorage.removeItem("dsf_remember_email");
      }

      navigate("/dashboard");
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Login failed. Please check your credentials.";
      setLoginError(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoginError("");
    setFormMessage("");

    if (!email.trim()) {
      setLoginError("Please enter your email.");
      setIsSubmitting(false);
      return;
    }

    try {
      const normalizedLoginApiUrl = getLoginApiUrl();

      const response = await axios.post(`${normalizedLoginApiUrl}/auth/forgot-password`, {
        email: email.trim(),
      });

      const data = response?.data || {};
      const token =
        data.token ||
        data.resetToken ||
        data.data?.token ||
        data.data?.resetToken ||
        data.result?.token ||
        "";

      if (token) {
        setCapturedResetToken(token);
        setFormMessage("Reset token received. Set your new password.");
        setSearchParams({ mode: "reset" });
        return;
      }

      setFormMessage("Reset request sent. Token not returned in response.");
      setSearchParams({ mode: "forgot" });
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to send reset request. Please try again.";
      setLoginError(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoginError("");
    setFormMessage("");

    if (!resetToken) {
      setLoginError("Reset token is missing. Please open the reset link from your email.");
      setIsSubmitting(false);
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setLoginError("New password must be at least 6 characters.");
      setIsSubmitting(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setLoginError("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    try {
      const normalizedLoginApiUrl = getLoginApiUrl();

      await axios.post(`${normalizedLoginApiUrl}/auth/reset-password`, {
        token: resetToken,
        password: newPassword,
      });

      setFormMessage("Password updated successfully. Please sign in.");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setCapturedResetToken("");
      setShowPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setSearchParams({ mode: "sign-in" });
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to reset password. Please try again.";
      setLoginError(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formTitle =
    mode === "forgot" ? "Forgot password" : mode === "reset" ? "Set new password" : "Sign in";

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          borderRadius="lg"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
          sx={{
            background: brandColor,
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {formTitle}
          </MDTypography>
          <Grid container spacing={3} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={2}>
              <MDTypography component={MuiLink} href="#" variant="body1" color="white">
                <FacebookIcon color="inherit" />
              </MDTypography>
            </Grid>
            <Grid item xs={2}>
              <MDTypography component={MuiLink} href="#" variant="body1" color="white">
                <GitHubIcon color="inherit" />
              </MDTypography>
            </Grid>
            <Grid item xs={2}>
              <MDTypography component={MuiLink} href="#" variant="body1" color="white">
                <GoogleIcon color="inherit" />
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox
            component="form"
            role="form"
            onSubmit={
              mode === "forgot"
                ? handleForgotPassword
                : mode === "reset"
                ? handleResetPassword
                : handleLogin
            }
          >
            {mode !== "reset" && (
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="Email"
                  fullWidth
                  value={email}
                  onChange={(event) => {
                    const nextEmail = event.target.value;
                    setEmail(nextEmail);
                    if (rememberMe) {
                      if (nextEmail.trim()) {
                        localStorage.setItem("dsf_remember_email", nextEmail.trim());
                      } else {
                        localStorage.removeItem("dsf_remember_email");
                      }
                    }
                  }}
                />
              </MDBox>
            )}
            {mode === "sign-in" && (
              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  fullWidth
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          <Icon>{showPassword ? "visibility_off" : "visibility"}</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>
            )}

            {mode === "reset" && (
              <>
                <MDBox mb={2}>
                  <MDInput
                    type={showNewPassword ? "text" : "password"}
                    label="New password"
                    fullWidth
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => setShowNewPassword((prev) => !prev)}
                            aria-label={showNewPassword ? "Hide password" : "Show password"}
                          >
                            <Icon>{showNewPassword ? "visibility_off" : "visibility"}</Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type={showConfirmPassword ? "text" : "password"}
                    label="Confirm password"
                    fullWidth
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          >
                            <Icon>{showConfirmPassword ? "visibility_off" : "visibility"}</Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
              </>
            )}

            {mode === "sign-in" && (
              <MDBox display="flex" alignItems="center" ml={-1}>
                <Switch
                  checked={rememberMe}
                  onChange={handleSetRememberMe}
                  sx={{
                    "& .MuiSwitch-switchBase": {
                      color: "#fff",
                    },
                    "& .MuiSwitch-switchBase + .MuiSwitch-track": {
                      backgroundColor: `${brandColor} !important`,
                      opacity: "0.35 !important",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#fff",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: `${brandColor} !important`,
                      opacity: "1 !important",
                    },
                    "&:hover .MuiSwitch-track": { backgroundColor: brandColor },
                  }}
                />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  onClick={handleSetRememberMe}
                  sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                >
                  &nbsp;&nbsp;Remember me
                </MDTypography>
              </MDBox>
            )}

            {mode === "sign-in" && (
              <MDBox mt={2} textAlign="right">
                <MDTypography
                  variant="button"
                  sx={{ cursor: "pointer", userSelect: "none", color: brandColor }}
                  onClick={() => {
                    setLoginError("");
                    setFormMessage("");
                    setSearchParams({ mode: "forgot" });
                  }}
                >
                  Forgot password?
                </MDTypography>
              </MDBox>
            )}

            {loginError && (
              <MDBox mt={1}>
                <MDTypography variant="caption" color="error">
                  {loginError}
                </MDTypography>
              </MDBox>
            )}
            {formMessage && (
              <MDBox mt={1}>
                <MDTypography variant="caption" sx={{ color: brandColor }}>
                  {formMessage}
                </MDTypography>
              </MDBox>
            )}
            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                type="submit"
                disabled={isSubmitting}
                sx={{
                  background: brandColor,
                  color: "#fff",
                  "&:hover": { background: brandColor },
                  "&.Mui-disabled": {
                    background: brandColor,
                    color: "#fff",
                    opacity: 0.7,
                  },
                  width: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "70%" },
                  mx: "auto",
                  display: "block",
                }}
              >
                {isSubmitting
                  ? "Loading..."
                  : mode === "forgot"
                  ? "Send reset"
                  : mode === "reset"
                  ? "Update password"
                  : "sign in"}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              {mode === "sign-in" ? (
                <MDTypography variant="button" color="text">
                  Welcome back
                </MDTypography>
              ) : (
                <MDTypography
                  variant="button"
                  sx={{ cursor: "pointer", userSelect: "none", color: brandColor }}
                  onClick={() => {
                    setLoginError("");
                    setFormMessage("");
                    setCapturedResetToken("");
                    setSearchParams({ mode: "sign-in" });
                  }}
                >
                  Back to sign in
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
